import { CommonModule } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    IimAutocompleteModule,
    IimCardModule,
    IimMultiselectComponent,
    IimMultiselectPreferences,
} from '@iim-angular/components';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { BaseSiteLayoutModule } from '@lib/util';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, concatMap, debounceTime, forkJoin, map, Observable, of } from 'rxjs';
import { PurchasingGroupAssignableUser } from '../../models/purchasing-group-assignable-user.model';
import { PurchasingGroupMember } from '../../models/purchasing-group-member.model';
import { PurchasingGroupDataService } from '../../services/purchasing-group-data.service';

export class PurchasingGroupMembersDialogData {
    constructor(
        public readonly purchasingGroupId: string,
        public readonly members: readonly PurchasingGroupMember[],
        public readonly allActiveUsers: readonly PurchasingGroupAssignableUser[],
    ) {}
}

export class PurchasingGroupMembersDialogResult {
    constructor(public readonly updated: boolean) {}
}

@Component({
    selector: 'lib-purchasing-group-members-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        BaseSiteLayoutModule,
        TranslateModule,
        IimCardModule,
        IimAutocompleteModule,
        IimMultiselectComponent,
    ],
    templateUrl: './purchasing-group-members-dialog.component.html',
    styleUrls: ['./purchasing-group-members-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasingGroupMembersDialogComponent {
    private readonly dialogRef = inject(
        MatDialogRef<PurchasingGroupMembersDialogComponent, PurchasingGroupMembersDialogResult>,
    );
    private readonly data = inject<PurchasingGroupMembersDialogData>(MAT_DIALOG_DATA);
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly destroyRef = inject(DestroyRef);

    public readonly heading = marker('Verwaltung');
    public readonly icon = 'fas fa-users';
    public readonly saving = signal(false);
    public readonly pendingChange = signal(false);
    public readonly inProgress = computed(() => this.saving() || this.pendingChange());
    public readonly multiselectPreferences: IimMultiselectPreferences = {
        showSelectAll: true,
        removeLastValueOnBackspace: true,
    };

    public readonly selectableUsers: PurchasingGroupAssignableUser[] = this.buildSelectableUsers();
    public readonly membersControl = new FormControl<PurchasingGroupAssignableUser[]>(
        this.buildInitialSelectedUsers(),
        { nonNullable: true },
    );
    private readonly multiselect = viewChild.required<IimMultiselectComponent>('multiselect');
    private readonly membersValue = toSignal(this.membersControl.valueChanges, {
        initialValue: this.membersControl.value,
    });
    public readonly selectedMemberCount = computed(() => this.membersValue().length);

    private savedMemberIds: ReadonlySet<string> = new Set(this.data.members.map((member) => member.id));
    private dialogUpdated = false;

    constructor() {
        this.membersControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((users) => {
            this.pendingChange.set(this.hasUnsavedChanges(users.map((user) => user.id)));
        });

        this.membersControl.valueChanges
            .pipe(
                debounceTime(400),
                concatMap((users) => this.sync(users.map((user) => user.id))),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        afterNextRender(() => this.multiselect().openPanel());
    }

    public close(): void {
        if (this.inProgress()) {
            return;
        }
        this.dialogRef.close(new PurchasingGroupMembersDialogResult(this.dialogUpdated));
    }

    public removeAll(): void {
        if (this.inProgress() || this.membersControl.value.length === 0) {
            return;
        }
        this.membersControl.setValue([]);
    }

    private sync(selectedIds: readonly string[]): Observable<void> {
        const toAssign = selectedIds.filter((id) => !this.savedMemberIds.has(id));
        const toRemove = [...this.savedMemberIds].filter((id) => !selectedIds.includes(id));

        if (toAssign.length === 0 && toRemove.length === 0) {
            this.pendingChange.set(false);
            return of(void 0);
        }

        this.saving.set(true);
        const requests: Observable<void>[] = [];
        if (toAssign.length > 0) {
            requests.push(this.dataService.assignMembers(this.data.purchasingGroupId, toAssign));
        }
        if (toRemove.length > 0) {
            requests.push(this.dataService.removeMembers(this.data.purchasingGroupId, toRemove));
        }

        return forkJoin(requests).pipe(
            map(() => {
                this.savedMemberIds = new Set(selectedIds);
                this.dialogUpdated = true;
                this.pendingChange.set(false);
                this.saving.set(false);
            }),
            catchError((error) => {
                this.saving.set(false);
                this.rollback();
                const message = this.dataService.resolveErrorMessage(
                    error,
                    marker('Mitglieder konnten nicht gespeichert werden.'),
                );
                this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                return of(void 0);
            }),
        );
    }

    private rollback(): void {
        const usersById = new Map(this.selectableUsers.map((user) => [user.id, user]));
        const value = [...this.savedMemberIds]
            .map((id) => usersById.get(id))
            .filter((user): user is PurchasingGroupAssignableUser => user !== undefined);
        this.membersControl.setValue(value, { emitEvent: false });
        this.pendingChange.set(false);
    }

    private hasUnsavedChanges(selectedIds: readonly string[]): boolean {
        if (selectedIds.length !== this.savedMemberIds.size) {
            return true;
        }
        return selectedIds.some((id) => !this.savedMemberIds.has(id));
    }

    private buildSelectableUsers(): PurchasingGroupAssignableUser[] {
        const knownUserIds = new Set(this.data.allActiveUsers.map((user) => user.id));
        const syntheticInactiveUsers = this.data.members
            .filter((member) => !knownUserIds.has(member.id))
            .map((member) => this.toSyntheticAssignableUser(member));

        return this.sortUsers([...this.data.allActiveUsers, ...syntheticInactiveUsers]);
    }

    private buildInitialSelectedUsers(): PurchasingGroupAssignableUser[] {
        const usersById = new Map(this.selectableUsers.map((user) => [user.id, user]));
        return this.data.members
            .map((member) => usersById.get(member.id))
            .filter((user): user is PurchasingGroupAssignableUser => user !== undefined);
    }

    private toSyntheticAssignableUser(member: PurchasingGroupMember): PurchasingGroupAssignableUser {
        return new PurchasingGroupAssignableUser({
            id: member.id,
            firstName: '',
            surName: member.displayName,
            active: member.active ?? false,
        });
    }

    private sortUsers(users: PurchasingGroupAssignableUser[]): PurchasingGroupAssignableUser[] {
        return [...users].sort((left, right) =>
            left.valueDisplay.localeCompare(right.valueDisplay, 'de', { sensitivity: 'base' }),
        );
    }
}
